import {
  Question,
  QuestionTypeType,
  RatingQuestionType,
  AdjectivesForPropertyQuestionType,
  ReviewFormTemplateType,
} from 'Shared/types/ReviewFormTemplate.type';

import { ReviewTemplateQuestionErrorType, ReviewTemplateReviewFormErrorType } from 'Shared/types/ReviewTemplates.type';

const checkHeader = ({ question }: { question: Question }): ReviewTemplateQuestionErrorType => {
  if (!question.header || question.header === '') {
    return {
      type: 'questionError',
      key: question.key,
      label: question.type === QuestionTypeType.TITLE_DESCRIPTION ? 'Header is required' : 'Question title is required',
    };
  }
  return null;
};

const checkPropertyAndAdjectives = ({ question }: { question: Question }): ReviewTemplateQuestionErrorType => {
  if (question.type !== QuestionTypeType.ADJECTIVES_FOR_PROPERTY) return null;

  const adjectives = Object.values(question.adjectives).flat();

  if (adjectives.some(adjective => !adjective || adjective === ''))
    return {
      type: 'questionError',
      key: question.key,
      label: 'Attributes cannot be blank',
    };

  if (new Set(adjectives).size !== adjectives.length)
    return {
      type: 'questionError',
      key: question.key,
      label: 'Attributes must be unique',
    };

  if (adjectives.length === 0)
    return {
      type: 'questionError',
      key: question.key,
      label: 'Must have at least one attribute',
    };

  return null;
};

const checkReasoningExplanation = ({ question }: { question: RatingQuestionType }): ReviewTemplateQuestionErrorType => {
  if (question.type !== QuestionTypeType.RATING) return null;
  if (question.showExplanation && (!question.explanationDescription || question.explanationDescription === '')) {
    return {
      type: 'questionError',
      key: question.key,
      label: 'Question reasoning is required',
    };
  }
  return null;
};

export const getReviewTemplateQuestionErrors = ({
  question,
}: {
  question: Question;
}): Array<ReviewTemplateQuestionErrorType> => {
  const errors: Array<ReviewTemplateQuestionErrorType> = [
    checkHeader({ question }),
    checkReasoningExplanation({ question: question as RatingQuestionType }),
    checkPropertyAndAdjectives({ question: question as AdjectivesForPropertyQuestionType }),
  ];

  // filter out null items before returning
  return errors.filter(e => e);
};

export const getReviewFormTemplateErrors = ({
  reviewFormTemplate,
}: {
  reviewFormTemplate: ReviewFormTemplateType;
}): Array<ReviewTemplateReviewFormErrorType> => {
  if (!reviewFormTemplate) return [];
  if (reviewFormTemplate.questions.length > 0) return [];

  return [
    {
      type: 'reviewFormTemplateError',
      formType: reviewFormTemplate.formType,
      label: 'Must have at least one question',
    },
  ];
};

export const getReviewFormTemplateQuestionErrors = ({
  reviewFormTemplate,
}: {
  reviewFormTemplate: ReviewFormTemplateType;
}): Array<ReviewTemplateQuestionErrorType> => {
  if (!reviewFormTemplate) return [];

  const errors: Array<ReviewTemplateQuestionErrorType> = reviewFormTemplate.questions
    .map(q => getReviewTemplateQuestionErrors({ question: q }))
    .flat();

  return errors.filter(e => e);
};
